import { computed } from 'vue';
import countries from 'shared/constants/countries.js';

/**
 * Composable for handling contact metadata formatting and display
 * @param {Object} contact - The contact object
 * @returns {Object} Contact metadata utilities
 */
export function useContactMetadata(contact) {
  /**
   * Contact initials computation
   */
  const contactInitials = computed(() => {
    if (!contact.value?.name) return '';
    return contact.value.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  });

  /**
   * Additional attributes with defaults
   */
  const additionalAttributes = computed(() => {
    return contact.value?.additional_attributes || {};
  });

  /**
   * Social profiles extraction
   */
  const socialProfiles = computed(() => {
    const {
      social_profiles: socialProfiles,
      screen_name: twitterScreenName,
      social_telegram_user_name: telegramUsername,
    } = additionalAttributes.value;

    return {
      twitter: socialProfiles?.twitter || twitterScreenName || '',
      telegram: socialProfiles?.telegram || telegramUsername || '',
      facebook: socialProfiles?.facebook || '',
      instagram: socialProfiles?.instagram || '',
      linkedin: socialProfiles?.linkedin || '',
      github: socialProfiles?.github || '',
    };
  });

  /**
   * Check if contact has social profiles
   */
  const hasSocialProfiles = computed(() => {
    return Object.values(socialProfiles.value).some(profile => !!profile);
  });

  /**
   * Format location with country flag
   */
  const location = computed(() => {
    const {
      country = '',
      city = '',
      country_code: countryCode,
    } = additionalAttributes.value;
    
    const cityAndCountry = [city, country].filter(item => !!item).join(', ');

    if (!cityAndCountry) {
      return '';
    }
    return findCountryFlag(countryCode, cityAndCountry);
  });

  /**
   * Normalized additional attributes with country code resolution
   */
  const normalizedAdditionalAttributes = computed(() => {
    const attrs = additionalAttributes.value;
    
    // If we have a country name but no countryCode, try to find the countryCode
    if (attrs.country && !attrs.countryCode) {
      const foundCountry = countries.find(c => c.name === attrs.country);
      if (foundCountry) {
        return {
          ...attrs,
          countryCode: foundCountry.id,
          socialProfiles: socialProfiles.value
        };
      }
    }
    
    return {
      ...attrs,
      socialProfiles: socialProfiles.value
    };
  });

  /**
   * Helper function to add country flag to location
   */
  function findCountryFlag(countryCode, cityAndCountry) {
    try {
      if (!countryCode) {
        return `${cityAndCountry} 🌎`;
      }

      const code = countryCode?.toLowerCase();
      return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Copy text to clipboard utility
   */
  function copyToClipboard(text) {
    if (!text) return;
    
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => false);
  }

  return {
    contactInitials,
    additionalAttributes,
    socialProfiles,
    hasSocialProfiles,
    location,
    normalizedAdditionalAttributes,
    copyToClipboard,
  };
}